import React, { useState, useEffect, useMemo } from 'react';
import URLHelper from '../../helpers/url';
import api from '../../services/api';
import { ResponsiveBar } from '@nivo/bar';
import dashboard from '../../helpers/dashboard';
import MenuContainer from '../../components/Dashboard/MenuContainer';
import MenuRadio from '../../components/Dashboard/MenuRadio';
import MenuDropdown from '../../components/Html/MenuDropdown';
import MenuGroupMode from '../../components/Dashboard/MenuGroupMode';
import MenuCheck from '../../components/Dashboard/MenuCheck';

export default function EficienciaOperacional({ filtros, metadados }) {

  const [isLoading, setIsLoading] = useState(false);
  const [loadHttpMsg, setLoadHttpMsg] = useState();
  const [data, setData] = useState();
  const [keys, setKeys] = useState();
  const colors = ['#1F9315', '#616171', '#FFCC01', '#E25C3B', '#D6D6D6'];
  const margin = { top: 50, right: 180, bottom: 150, left: 40 };
  const [groupMode, setGroupMode] = useState('stacked');
  const [indice, setIndice] = useState('maquina');
  const [clientes, setClientes] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [porMes, setPorMes] = useState(false);
  const [emPorcentagem, setEmPorcentagem] = useState(true);

  const dadosFiltrados = data 
    ? filtrar(data.data) 
    : { eventos: [], disponibilidades: [] };

  const dadosComputados = useMemo(() => {
    if ( ! data) {
      return;
    }
    let dados = calcular(dadosFiltrados);
    if (emPorcentagem) {
      dados = converteParaPorcentagem(dados);
    }
    return dados;
  }, [dadosFiltrados]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let k = [
      "Produção",
      "Setup", 
      "Parada Programada", 
      "Parada Não Programada",
    ];
    if (indice !== 'operador') {
      k.push("Sem Apontamentos");
    }
    setKeys(k);
  }, [indice]);

  async function loadData() {
    setLoadHttpMsg(null);
    let query = URLHelper.createQueryStringFromObject({
      periodoInicial: filtros.periodoInicial,
      periodoFinal: filtros.periodoFinal,
    }).toString();
    let resp;
    try {
      setIsLoading(true);
      resp = await api.get(`/relatorios/estamparia/eficiencia-operacional?` + query);
    } catch (error) {
      setIsLoading(false);
      if (error.response.status < 500) {
        setLoadHttpMsg(error.response.data.description);
      } else {
        setLoadHttpMsg("Ocorreu um erro no servidor e não foi possível carregar o gráfico. Por favor, tente novamente.");
      }
      return;
    }
    let data = resp.data;
    setClientes(extraiIndice(data.data.clientes));
    setOperadores(extraiIndice(data.data.operadores));
    setMaquinas(extraiIndice(data.data.maquinas));
    setData(data);
    setIsLoading(false);
  }

  function filtrar(dados) {
    let clientesEscolhidos = clientes.filter(c => c.checked).map(c => c.id);
    let operadoresEscolhidos = operadores.filter(o => o.checked).map(o => o.id);
    let maquinasEscolhidas = maquinas.filter(m => m.checked).map(m => m.id);
    let eventosFiltrados = dados.eventos.filter(e => {
      return clientesEscolhidos.includes(e.clienteId)
        && operadoresEscolhidos.includes(e.operadorId)
        && maquinasEscolhidas.includes(e.maquinaId);
    });
    let disponibilidadesFiltradas = dados.disponibilidades.filter(e => {
      return clientesEscolhidos.includes(e.clienteId)
        && maquinasEscolhidas.includes(e.maquinaId);
    });
    return {
      eventos: eventosFiltrados,
      disponibilidades: disponibilidadesFiltradas
    }
  }

  function extraiIndice(data) {
    let indices = [];
    for (const row of data) {
      indices.push({
        id: row.id,
        label: row.nome,
        checked: true
      });
    }
    return dashboard.ordenaCrescente(indices, 'label');
  }

  function calcular({ eventos, disponibilidades }) {
    let resultado = [];
    let meses = dashboard.periodoDeMeses(data.filtrosAplicados.periodoInicial, data.filtrosAplicados.periodoFinal);
    let template = {
      index: null,
      "Setup": 0,
      "Parada Programada": 0,
      "Parada Não Programada": 0,
      "Produção": 0,
      "Sem Apontamentos": 0,
      totalEventosSegundos: 0,
      disponibilidadeMaquinaSegundos: 0
    }
    if (porMes) {
      meses.map(anoMes => {
        anoMes = parseInt(anoMes);
        let acumulador = { ...template };
        acumulador.index = (anoMes+"").substring(4,6)+'/'+(anoMes+"").substring(0,4);
        eventos
          .filter(evento => evento.anoMes === anoMes)
          .map(evento => somaEvento(acumulador, evento));
        disponibilidades
          .filter(disponibilidade => disponibilidade.anoMes === anoMes)
          .map(disponibilidade => somaDisponibilidade(acumulador, disponibilidade));
        resultado.push(acumulador);
      });
    } else {
      let plurais = {
        'maquina': 'maquinas',
        'cliente': 'clientes',
        'operador': 'operadores'
      }
      let indicePlural = plurais[indice];
      let indiceId = indice + 'Id';
      eval(indicePlural).filter(item => item.checked).map(item => {
        let acumulador = { ...template };
        acumulador.index = eval('data.data.'+indicePlural).find(i => i.id === item.id).nome;
        eventos
          .filter(e => e[indiceId] === item.id)
          .map(e => somaEvento(acumulador, e));
        disponibilidades
          .filter(d => d[indiceId] === item.id)
          .map(d => somaDisponibilidade(acumulador, d));
        resultado.push(acumulador);
      });
    }
    return resultado;
  }

  function somaEvento(acumulador, evento) {
    acumulador["Setup"] += dashboard.converteSegundosParaHoras(evento.setupSegundos);
    acumulador["Parada Programada"] += dashboard.converteSegundosParaHoras(evento.paradaProgramadaSegundos);
    acumulador["Parada Não Programada"] += dashboard.converteSegundosParaHoras(evento.paradaNaoProgramadaSegundos);
    acumulador["Produção"] += dashboard.converteSegundosParaHoras(evento.producaoSegundos);
    acumulador.totalEventosSegundos += dashboard.converteSegundosParaHoras(evento.setupSegundos + evento.paradaProgramadaSegundos + evento.paradaNaoProgramadaSegundos + evento.producaoSegundos);
  }

  function somaDisponibilidade(acumulador, disponibilidade) {
    acumulador.disponibilidadeMaquinaSegundos += dashboard.converteSegundosParaHoras(disponibilidade.disponibilidadeSegundos);
    if (indice !== "operador") {
      acumulador["Sem Apontamentos"] += dashboard.converteSegundosParaHoras(disponibilidade.semApontamentosSegundos);
    }
  }

  function converteParaPorcentagem(resultado) {
    let divisor = 'disponibilidadeMaquinaSegundos';
    if (indice === 'operador') {
      divisor = 'totalEventosSegundos';
    }
    return resultado.map(r => {
      if (r[divisor] === 0) {
        return r;
      }
      r["Setup"] = (r["Setup"] * 100) / r[divisor];
      r["Parada Programada"] = (r["Parada Programada"] * 100) / r[divisor];
      r["Parada Não Programada"] = (r["Parada Não Programada"] * 100) / r[divisor];
      r["Produção"] = (r["Produção"] * 100) / r[divisor];
      if (indice !== "operador") {
        r["Sem Apontamentos"] = (r["Sem Apontamentos"] * 100) / r[divisor];
      }
      return r;
    });
  }

  if (isLoading) {
    return dashboard.alertLoading()
  }

  if (loadHttpMsg) {
    return dashboard.alert(loadHttpMsg);
  }

  return (
    <>
      <MenuContainer 
        groups={[
          [
            <MenuRadio 
              values={[{
                  id: 'maquina',
                  label: 'Máquina',
                }, {
                  id: 'cliente',
                  label: 'Cliente'
                }, {
                  id: 'operador',
                  label: 'Operador'
                }
              ]}
              checked={indice}
              onChange={novoIndex => {
                setIndice(novoIndex)
              }}
            />
          ],
          [
            <MenuDropdown
              title="Máquinas"
              values={maquinas}
              setValues={setMaquinas}
            />,
            <MenuDropdown
              title="Clientes"
              values={clientes}
              setValues={setClientes}
            />,
            <MenuDropdown
              title="Operadores"
              values={operadores}
              setValues={setOperadores}
            />
          ],
          [
            <MenuGroupMode
              checked={groupMode}
              onChange={mode => setGroupMode(mode)}
            />
          ],
          [
            <MenuCheck 
              label="Mês"
              checked={porMes}
              onChange={setPorMes}
            />,
            <MenuCheck 
              label="%"
              checked={emPorcentagem}
              onChange={setEmPorcentagem}
            />
          ]
        ]}
      />

      <h3 className="dashboard-title">Eficiência Operacional</h3>
      {dadosComputados && dadosComputados.length > 0 &&
        <div className="dashboard-chart-description">
          <table className="m-auto">
            <tbody>
              <tr>
                <td className="text-right pr-2">Período:</td>
                <td className="text-left">{new Date(data.filtrosAplicados.periodoInicial).toLocaleDateString()} a {new Date(data.filtrosAplicados.periodoFinal).toLocaleDateString()}</td>
              </tr>
              <tr>
                <td className="text-right pr-2">Disponibilidade de Máquina:</td>
                <td className="text-left">{Number(dashboard.converteSegundosParaHoras(dadosFiltrados.disponibilidades.reduce((acc, curr) => acc + curr.disponibilidadeSegundos, 0))).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h</td>
              </tr>
              <tr>
                <td className="text-right pr-2">Produção:</td>
                <td className="text-left">{Number(dashboard.converteSegundosParaHoras(dadosFiltrados.eventos.reduce((acc, curr) => acc + curr.producaoSegundos, 0))).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h</td>
              </tr>
              <tr>
                <td className="text-right pr-2">Setup:</td>
                <td className="text-left">{Number(dashboard.converteSegundosParaHoras(dadosFiltrados.eventos.reduce((acc, curr) => acc + curr.setupSegundos, 0))).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h</td>
              </tr>
              <tr>
                <td className="text-right pr-2">Parada Programada:</td>
                <td className="text-left">{Number(dashboard.converteSegundosParaHoras(dadosFiltrados.eventos.reduce((acc, curr) => acc + curr.paradaProgramadaSegundos, 0))).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h</td>
              </tr>
              <tr>
                <td className="text-right pr-2">Parada Não Programada:</td>
                <td className="text-left">{Number(dashboard.converteSegundosParaHoras(dadosFiltrados.eventos.reduce((acc, curr) => acc + curr.paradaNaoProgramadaSegundos, 0))).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h</td>
              </tr>
            </tbody>
          </table>
        </div>
      }
      <div style={{ height: '600px', minWidth: dashboard.calculaLargura(70, keys || [], dadosFiltrados || [], groupMode, margin) }}>
        <ResponsiveBar
          data={dadosComputados}
          keys={keys}
          groupMode={groupMode}
          indexBy="index"
          margin={margin}
          padding={0.3}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={colors}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          labelFormat={v => emPorcentagem ? Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })+' %' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 })+' h'}
          valueFormat={v => emPorcentagem ? Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })+' %' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 })+' h'}
          legends={[
            {
              dataFrom: 'keys',
              toggleSerie: true,
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 2,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: 'left-to-right',
              itemOpacity: 1,
              symbolSize: 20,
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legendPosition: 'middle',
            legendOffset: 32
          }}
          axisLeft={{
            format: v => emPorcentagem ? Number(v).toLocaleString('pt-BR')+' %' : Number(v).toLocaleString('pt-BR')+' h',
          }}
          axisTop={{
            tickSize: 5,
            tickPadding: 20,
            legend: '',
            legendPosition: 'middle',
            legendOffset: 32,
            format: function(value) {
              let row = dadosComputados.find(d => d.index === value);
              if ( ! row) {
                return '';
              }
              if (indice === 'operador') {
                let axisValue = row.totalEventosSegundos;
                return <>
                  <tspan x="0" dy="0">Eventos</tspan>
                  <tspan x="0" dy="1.2em">{Number(axisValue).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h</tspan>
                </>;
              }
              let axisValue = row.disponibilidadeMaquinaSegundos;
              return <>
                <tspan x="0" dy="0">Disp. Máq.</tspan>
                <tspan x="0" dy="1.2em">{Number(axisValue).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h</tspan>
              </>;
            },
          }}
          tooltip={({ id, indexValue, value, color }) => {
            return <>
              <div style={{ padding: 5, background: '#fff' }}>
                <div style={{backgroundColor:color, display:'inline-block', height:'15px', marginRight:'10px', width:'20px'}}></div>
                {id}: {emPorcentagem ? Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 2 })+' %' : Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 2 })+' h'}
              </div>
            </>
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor="blacks"
          motionStiffness={90}
          motionDamping={15}
          theme={{
            "grid": {
              "line": {
                  "stroke": "#a8a8a8",
                  "strokeWidth": 1
              },
            },
            "axis": {
              "ticks": {
                "text": {
                  "fontSize": 12,
                  "fill": "#000"
                }
              }
            }
          }}
        />
      </div>
    </>
  );
}